import type {
  InsightItemDto,
  ReportDetailDto,
  ReportSectionDto,
  UpdateReportSectionRequestDto,
} from '@act/shared/data-transfer-objects';
import {
  normalizeReportCalloutTitleForStorage,
  resolveEffectiveSectionInsights,
  sectionDefaultCalloutForSection,
} from '@act/shared/data-transfer-objects';
import { arrayMove } from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';

type PatchSectionFn = (args: {
  params: { id: string; sectionRowId: string };
  body: UpdateReportSectionRequestDto;
}) => void;

type ReorderSectionsFn = (args: {
  params: { id: string };
  body: {
    sections: { sectionRowId: string; order: number }[];
  };
}) => void;

type InsightCardKey = 'clinical' | 'financial';
type InsightFieldKey = 'heading' | 'body';
type SectionInsightsPatch = NonNullable<ReportSectionDto['insights']>;

const INSIGHT_PERSIST_DEBOUNCE_MS = 500;

const buildInsightsPatch = (
  section: ReportSectionDto,
  dataSnapshot: Record<string, unknown> | null,
  card: InsightCardKey,
  field: InsightFieldKey,
  value: string,
): SectionInsightsPatch => {
  const effective = resolveEffectiveSectionInsights(
    section.insights,
    dataSnapshot,
  );
  const fromEffective = effective?.[card];
  const baseCard: InsightItemDto = {
    heading: fromEffective?.heading ?? '',
    body: fromEffective?.body ?? '',
    citationUrl: fromEffective?.citationUrl,
    citationLabel: fromEffective?.citationLabel,
  };
  const fromSection = section.insights?.[card];
  return {
    ...(section.insights ?? {}),
    [card]: {
      ...baseCard,
      ...fromSection,
      [field]: value,
    },
  };
};

/**
 * Local section state and persistence handlers for the report builder.
 * @param reportId - Report uuid from the route.
 * @param report - Loaded report detail (null while loading).
 * @param patchSection - API patch for a single section row.
 * @param reorderSections - API reorder endpoint.
 * @returns Section list state, selection, and change handlers.
 */
export const useReportBuilderSections = (
  reportId: string,
  report: ReportDetailDto | null,
  patchSection: PatchSectionFn,
  reorderSections: ReorderSectionsFn,
) => {
  const [localSections, setLocalSections] = useState<ReportSectionDto[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );

  const fieldPersistTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const pendingInsightsRef = useRef<Record<string, SectionInsightsPatch>>({});
  const localSectionsRef = useRef(localSections);

  useEffect(() => {
    localSectionsRef.current = localSections;
  }, [localSections]);

  useEffect(() => {
    const timers = fieldPersistTimersRef.current;
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (!report) {
      return;
    }
    const sorted = [...report.sections].sort((a, b) => a.order - b.order);
    setLocalSections(sorted);
    setSelectedSectionId(current => current ?? sorted[0]?.id ?? null);
  }, [report]);

  const selectedSection = useMemo(
    () => localSections.find(section => section.id === selectedSectionId),
    [localSections, selectedSectionId],
  );

  const persistSection = (
    sectionRowId: string,
    body: UpdateReportSectionRequestDto,
  ) => {
    patchSection({ params: { id: reportId, sectionRowId }, body });
  };

  const updateSectionLocal = (
    sectionRowId: string,
    patch: Partial<ReportSectionDto>,
  ) => {
    setLocalSections(current =>
      current.map(section =>
        section.id === sectionRowId ? { ...section, ...patch } : section,
      ),
    );
  };

  const getSectionForInsightEdit = (
    sectionId: string,
  ): ReportSectionDto | undefined => {
    const row = localSectionsRef.current.find(section => section.id === sectionId);
    if (!row) {
      return undefined;
    }
    const pending = pendingInsightsRef.current[sectionId];
    if (!pending) {
      return row;
    }
    return { ...row, insights: pending };
  };

  const scheduleInsightPersist = (
    sectionId: string,
    nextInsights: SectionInsightsPatch,
  ) => {
    pendingInsightsRef.current[sectionId] = nextInsights;

    const key = `${sectionId}:insights`;
    const pending = fieldPersistTimersRef.current[key];
    if (pending) {
      clearTimeout(pending);
    }
    fieldPersistTimersRef.current[key] = setTimeout(() => {
      const insights = pendingInsightsRef.current[sectionId];
      if (insights) {
        updateSectionLocal(sectionId, { insights });
        persistSection(sectionId, { insights });
        delete pendingInsightsRef.current[sectionId];
      }
      delete fieldPersistTimersRef.current[key];
    }, INSIGHT_PERSIST_DEBOUNCE_MS);
  };

  const handleIncludedChange = (
    section: ReportSectionDto,
    included: boolean,
  ) => {
    updateSectionLocal(section.id, { included });
    persistSection(section.id, { included });
  };

  const handleCalloutEnabledChange = (
    section: ReportSectionDto,
    calloutEnabled: boolean,
  ) => {
    const defaults = sectionDefaultCalloutForSection(section.sectionId);
    const patch: UpdateReportSectionRequestDto = { calloutEnabled };
    if (calloutEnabled && defaults) {
      if (!section.callout?.trim()) {
        patch.callout = defaults.body;
      }
      if (!section.calloutTitle?.trim()) {
        patch.calloutTitle = defaults.title;
      }
    }
    updateSectionLocal(section.id, {
      calloutEnabled,
      ...(patch.callout !== undefined ? { callout: patch.callout } : {}),
      ...(patch.calloutTitle !== undefined
        ? { calloutTitle: patch.calloutTitle }
        : {}),
    });
    persistSection(section.id, patch);
  };

  const handleClinicalInsightEnabledChange = (
    section: ReportSectionDto,
    clinicalInsightEnabled: boolean,
  ) => {
    updateSectionLocal(section.id, { clinicalInsightEnabled });
    persistSection(section.id, { clinicalInsightEnabled });
  };

  const handleFinancialInsightEnabledChange = (
    section: ReportSectionDto,
    financialInsightEnabled: boolean,
  ) => {
    updateSectionLocal(section.id, { financialInsightEnabled });
    persistSection(section.id, { financialInsightEnabled });
  };

  const handleFieldBlur = (
    section: ReportSectionDto,
    field: 'narrative' | 'callout' | 'calloutTitle',
    value: string,
  ) => {
    const key = `${section.id}:${field}`;
    const pending = fieldPersistTimersRef.current[key];
    if (pending) {
      clearTimeout(pending);
      delete fieldPersistTimersRef.current[key];
    }
    const normalized =
      field === 'calloutTitle'
        ? normalizeReportCalloutTitleForStorage(value)
        : value.trim() === ''
          ? null
          : value.trim();
    const current = section[field];
    const currentNormalized =
      field === 'calloutTitle'
        ? normalizeReportCalloutTitleForStorage(current ?? '')
        : current === null || current.trim() === ''
          ? null
          : current.trim();
    if (currentNormalized === normalized) {
      return;
    }
    updateSectionLocal(section.id, { [field]: normalized });
    persistSection(section.id, { [field]: normalized });
  };

  const flushInsightPersist = (
    section: ReportSectionDto,
    insights: SectionInsightsPatch,
  ) => {
    const key = `${section.id}:insights`;
    const pending = fieldPersistTimersRef.current[key];
    if (pending) {
      clearTimeout(pending);
      delete fieldPersistTimersRef.current[key];
    }
    persistSection(section.id, { insights });
  };

  const handleInsightFieldChange = (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: InsightCardKey,
    field: InsightFieldKey,
    value: string,
  ) => {
    const currentSection = getSectionForInsightEdit(section.id);
    if (!currentSection) {
      return;
    }
    const nextInsights = buildInsightsPatch(
      currentSection,
      dataSnapshot,
      card,
      field,
      value,
    );
    scheduleInsightPersist(section.id, nextInsights);
  };

  const handleInsightFieldBlur = (
    section: ReportSectionDto,
    dataSnapshot: Record<string, unknown> | null,
    card: InsightCardKey,
    field: InsightFieldKey,
    value: string,
  ) => {
    const key = `${section.id}:insights`;
    const pending = fieldPersistTimersRef.current[key];
    if (pending) {
      clearTimeout(pending);
      delete fieldPersistTimersRef.current[key];
    }

    const currentSection = getSectionForInsightEdit(section.id);
    if (!currentSection) {
      delete pendingInsightsRef.current[section.id];
      return;
    }

    const effective = resolveEffectiveSectionInsights(
      currentSection.insights,
      dataSnapshot,
    );
    const currentValue = currentSection.insights?.[card]?.[field]
      ?? effective?.[card]?.[field]
      ?? '';
    if (currentValue.trim() === value.trim()) {
      delete pendingInsightsRef.current[section.id];
      return;
    }

    const nextInsights = buildInsightsPatch(
      currentSection,
      dataSnapshot,
      card,
      field,
      value.trim(),
    );
    delete pendingInsightsRef.current[section.id];
    updateSectionLocal(section.id, { insights: nextInsights });
    flushInsightPersist(section, nextInsights);
  };

  const handleReorder = (activeId: string, overId: string) => {
    const oldIndex = localSections.findIndex(section => section.id === activeId);
    const newIndex = localSections.findIndex(section => section.id === overId);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const reordered = arrayMove(localSections, oldIndex, newIndex).map(
      (section, orderIndex) => ({
        ...section,
        order: orderIndex + 1,
      }),
    );
    setLocalSections(reordered);
    reorderSections({
      params: { id: reportId },
      body: {
        sections: reordered.map(section => ({
          sectionRowId: section.id,
          order: section.order,
        })),
      },
    });
  };

  return {
    localSections,
    selectedSectionId,
    setSelectedSectionId,
    selectedSection,
    handleIncludedChange,
    handleCalloutEnabledChange,
    handleClinicalInsightEnabledChange,
    handleFinancialInsightEnabledChange,
    handleFieldBlur,
    handleInsightFieldChange,
    handleInsightFieldBlur,
    handleReorder,
  };
};
